import {
  ArrowRight,
  ExternalLink,
  Flag,
  LifeBuoy,
  Megaphone,
  RefreshCw,
  Settings2,
} from "lucide-react";
import { Link } from "react-router-dom";

import {
  useSystemOpsAIInventory,
  useSystemOpsAutomationSummary,
} from "@/lib/api/adminHooks";
import { useBootstrap } from "@/lib/api/hooks";

type FullAdminRole = "admin" | "legacy_admin";

interface InternalDestination {
  title: string;
  description: string;
  to: string;
  authority: string;
  roles: FullAdminRole[];
  icon: React.ReactNode;
}

interface ExternalDestination {
  title: string;
  description: string;
  href: string;
  authority: string;
  caution: string;
  roles: FullAdminRole[];
}

const INTERNAL_DESTINATIONS: InternalDestination[] = [
  {
    title: "Support exceptions and metrics",
    description: "First-party ticket queue, views, settings, and reporting.",
    to: "/admin/support",
    authority: "System Labs backend",
    roles: ["admin", "legacy_admin"],
    icon: <LifeBuoy size={18} aria-hidden />,
  },
  {
    title: "Lifecycle campaigns",
    description:
      "Campaign definitions, enrollment state, and the existing builder.",
    to: "/admin/lifecycle-emails",
    authority: "Lifecycle APIs; ActiveCampaign delivers sequences",
    roles: ["admin", "legacy_admin"],
    icon: <Megaphone size={18} aria-hidden />,
  },
  {
    title: "Feature flags",
    description:
      "Existing registered flags. This landing page never treats flags as health.",
    to: "/admin/feature-flags",
    authority: "Backend feature-flag registry",
    roles: ["admin", "legacy_admin"],
    icon: <Flag size={18} aria-hidden />,
  },
];

const EXTERNAL_DESTINATIONS: ExternalDestination[] = [
  {
    title: "Intercom",
    description: "Authoritative human customer-support inbox after cutover.",
    href: "https://app.intercom.com/",
    authority: "Conversations, assignment, state, and staff replies",
    caution: "Requires a separately authorized Intercom seat.",
    roles: ["admin", "legacy_admin"],
  },
  {
    title: "SalesMessage",
    description: "Current SMS provider and operator surface.",
    href: "https://app.salesmessage.com/",
    authority: "SMS inbox, consent state, sending number, and human replies",
    caution:
      "Keep until SMS authority and rollback evidence approve retirement.",
    roles: ["admin", "legacy_admin"],
  },
  {
    title: "Plain archive",
    description: "Legacy support history and migration reconciliation only.",
    href: "https://app.plain.com/",
    authority: "Legacy support archive during migration acceptance",
    caution: "Read-only migration use; not an independent live send path.",
    roles: ["admin", "legacy_admin"],
  },
  {
    title: "Sanity Studio",
    description:
      "Authoring for public content, products, landers, and experiments.",
    href: "https://www.sanity.io/manage",
    authority: "Marketing content and publication workflow",
    caution: "Requires a separately authorized Sanity account.",
    roles: ["admin", "legacy_admin"],
  },
  {
    title: "Stripe",
    description: "Authoritative payment and subscription dashboard.",
    href: "https://dashboard.stripe.com/",
    authority: "Payments, invoices, subscriptions, disputes, and refunds",
    caution: "Finance access only; no customer or payment details appear here.",
    roles: ["admin", "legacy_admin"],
  },
  {
    title: "ActiveCampaign",
    description: "Delivery system for lifecycle campaign automations.",
    href: "https://www.activecampaign.com/login",
    authority: "Contacts, tags, sequences, and delivery behavior",
    caution:
      "Provider actions can send real messages; this link adds no authority.",
    roles: ["admin", "legacy_admin"],
  },
];

function effectiveRole(role: string | null | undefined): FullAdminRole | null {
  if (role === "admin") return "admin";
  if (role === null) return "legacy_admin";
  return null;
}

function StatusBox({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <article className="border border-line rounded-lg bg-raised p-14 flex flex-col gap-6">
      <h3 className="type-label-sm font-semibold">{title}</h3>
      {children}
    </article>
  );
}

export function SystemOpsScreen() {
  const { data: bootstrap } = useBootstrap();
  const role = effectiveRole(bootstrap?.admin_role);
  const ai = useSystemOpsAIInventory();
  const automations = useSystemOpsAutomationSummary();
  const visibleInternal = role
    ? INTERNAL_DESTINATIONS.filter((destination) =>
        destination.roles.includes(role),
      )
    : [];
  const visibleExternal = role
    ? EXTERNAL_DESTINATIONS.filter((destination) =>
        destination.roles.includes(role),
      )
    : [];
  const partialFailure = ai.isError !== automations.isError;

  return (
    <div className="flex flex-col gap-24">
      <header className="flex flex-col gap-8">
        <div className="flex items-center gap-8">
          <Settings2 size={22} aria-hidden />
          <h1 className="type-heading-xl">System Operations</h1>
        </div>
        <p className="type-body-md text-ink-muted max-w-content-sm">
          One read-only directory for finding the systems System Labs already
          uses. It does not add tools, permissions, provider access, AI routing,
          or runtime controls.
        </p>
      </header>

      <section
        aria-labelledby="status-heading"
        className="flex flex-col gap-10"
      >
        <div>
          <h2 id="status-heading" className="type-heading-lg">
            Read-only status
          </h2>
          <p className="type-body-sm text-ink-muted mt-4">
            Each source loads independently. AI runtime health is not available
            from the current registry and is never inferred from flags.
          </p>
        </div>

        {partialFailure && (
          <div
            role="alert"
            className="border border-warning/30 bg-warning-wash p-12"
          >
            <p className="type-label-sm text-warning">
              One status source is unavailable. The available directory and
              status source are still shown below.
            </p>
          </div>
        )}

        <div className="grid gap-10 md:grid-cols-2">
          <StatusBox title="AI capability inventory">
            {ai.isLoading && (
              <p role="status" className="type-body-sm text-ink-muted">
                Loading AI inventory…
              </p>
            )}
            {ai.isError && (
              <div role="alert" className="flex flex-col items-start gap-6">
                <p className="type-body-sm text-danger">
                  AI inventory unavailable.
                </p>
                <button
                  type="button"
                  onClick={() => void ai.refetch()}
                  className="inline-flex items-center gap-5 rounded border border-line px-9 py-5 type-label-sm hover:bg-sunken"
                >
                  <RefreshCw size={14} aria-hidden /> Retry AI inventory
                </button>
              </div>
            )}
            {ai.data && (
              <dl className="grid gap-5 type-body-sm">
                <div className="flex justify-between gap-8">
                  <dt className="text-ink-muted">Catalog</dt>
                  <dd>{ai.data.catalogState}</dd>
                </div>
                <div className="flex justify-between gap-8">
                  <dt className="text-ink-muted">Schema</dt>
                  <dd>v{ai.data.schemaVersion}</dd>
                </div>
                <div className="flex justify-between gap-8">
                  <dt className="text-ink-muted">Runtime status</dt>
                  <dd>Unavailable</dd>
                </div>
              </dl>
            )}
          </StatusBox>

          <StatusBox title="Backend automations">
            {automations.isLoading && (
              <p role="status" className="type-body-sm text-ink-muted">
                Loading automation status…
              </p>
            )}
            {automations.isError && (
              <div role="alert" className="flex flex-col items-start gap-6">
                <p className="type-body-sm text-danger">
                  Automation status unavailable.
                </p>
                <button
                  type="button"
                  onClick={() => void automations.refetch()}
                  className="inline-flex items-center gap-5 rounded border border-line px-9 py-5 type-label-sm hover:bg-sunken"
                >
                  <RefreshCw size={14} aria-hidden /> Retry automations
                </button>
              </div>
            )}
            {automations.data && (
              <dl className="grid gap-5 type-body-sm">
                <div className="flex justify-between gap-8">
                  <dt className="text-ink-muted">Catalog</dt>
                  <dd>{automations.data.catalogState}</dd>
                </div>
                <div className="flex justify-between gap-8">
                  <dt className="text-ink-muted">Explicit health evidence</dt>
                  <dd>{automations.data.healthEvidence}</dd>
                </div>
                <div className="flex justify-between gap-8">
                  <dt className="text-ink-muted">Reported health</dt>
                  <dd className="capitalize">
                    {automations.data.healthStatus}
                  </dd>
                </div>
              </dl>
            )}
          </StatusBox>
        </div>
      </section>

      <section aria-labelledby="where-heading" className="flex flex-col gap-10">
        <div>
          <h2 id="where-heading" className="type-heading-lg">
            Where do I go?
          </h2>
          <p className="type-body-sm text-ink-muted mt-4">
            Each destination keeps its existing authorization and source of
            truth.
          </p>
        </div>
        {visibleInternal.length === 0 ? (
          <p className="border border-line rounded-lg bg-raised p-14 type-body-sm text-ink-muted">
            No System Operations destinations are available for this role.
          </p>
        ) : (
          <div className="grid gap-10 md:grid-cols-2 xl:grid-cols-3">
            {visibleInternal.map((destination) => (
              <Link
                key={destination.to}
                to={destination.to}
                className="group border border-line rounded-lg bg-raised p-14 flex flex-col gap-8 hover:bg-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                <div className="flex items-center justify-between gap-8">
                  <span className="text-ink-muted">{destination.icon}</span>
                  <ArrowRight
                    size={16}
                    className="text-ink-muted group-hover:text-ink"
                    aria-hidden
                  />
                </div>
                <div>
                  <h3 className="type-label-md font-semibold">
                    {destination.title}
                  </h3>
                  <p className="type-body-sm text-ink-muted mt-4">
                    {destination.description}
                  </p>
                </div>
                <p className="type-label-xs text-ink-muted mt-auto">
                  Source of truth: {destination.authority}
                </p>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section
        aria-labelledby="external-heading"
        className="flex flex-col gap-10"
      >
        <div>
          <h2 id="external-heading" className="type-heading-lg">
            External authorities
          </h2>
          <p className="type-body-sm text-ink-muted mt-4 max-w-content-sm">
            These links open provider sign-in pages. Provider membership still
            decides access; this page never embeds credentials, provider data,
            or provider controls.
          </p>
          <p className="type-body-sm text-ink-muted mt-4 max-w-content-sm">
            Engineering-only consoles such as Railway, Supabase, Vercel, GitHub,
            and Braintrust are intentionally not linked because the portal has
            no engineering or provider-seat entitlement.
          </p>
        </div>
        {visibleExternal.length === 0 ? (
          <p className="border border-line rounded-lg bg-raised p-14 type-body-sm text-ink-muted">
            No external authority links are available for this role.
          </p>
        ) : (
          <div className="grid gap-10 md:grid-cols-2 xl:grid-cols-3">
            {visibleExternal.map((destination) => (
              <a
                key={destination.title}
                href={destination.href}
                target="_blank"
                rel="noreferrer"
                className="border border-line rounded-lg bg-raised p-14 flex flex-col gap-7 hover:bg-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                <div className="flex items-center justify-between gap-8">
                  <h3 className="type-label-md font-semibold">
                    {destination.title}
                  </h3>
                  <ExternalLink
                    size={15}
                    aria-label="Opens external provider"
                  />
                </div>
                <p className="type-body-sm text-ink-muted">
                  {destination.description}
                </p>
                <p className="type-label-xs text-ink-muted">
                  Source of truth: {destination.authority}
                </p>
                <p className="type-body-sm border-l-2 border-line pl-8 mt-auto">
                  {destination.caution}
                </p>
              </a>
            ))}
          </div>
        )}
      </section>

      <div className="border border-line-subtle bg-sunken p-12 type-body-sm text-ink-muted">
        System Operations is a directory and read-only projection. It cannot
        approve actions, edit prompts, enable flags, change permissions, call
        providers, or expose patient, payment, or conversation data.
      </div>
    </div>
  );
}
